import { InfoOutlined } from "@mui/icons-material";
import { Box, Divider, Typography } from "@mui/material";
import BasicMenu from "./BasicMenu";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import LoadingIndicator from './LoadingIndicator';
import PlanoAcaoResultadoConsulta from "../types/negocio/PlanoAcaoResultadoConsulta";

interface CardsPlanoDeAcaoProps {
    readonly planos: PlanoAcaoResultadoConsulta[];
    readonly loading: boolean;
    readonly onArquivar: (guid: string) => void;
    readonly onEditar: (guid: string) => void;
}

export function CardsPlanoDeAcao({ planos, loading, onArquivar, onEditar }: CardsPlanoDeAcaoProps) {
    if (loading) {
        return <LoadingIndicator message="Carregando planos de ação..." />;
    }

    if (planos.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1">Nenhum plano de ação encontrado.</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
            }}
        >
            {planos.map((plano, index) => {
                const prazoFormatado = plano.prazoConclusao 
                    ? format(new Date(plano.prazoConclusao), 'dd/MM/yyyy', { locale: ptBR }) 
                    : '';
                
                return (
                <Box
                    key={plano.guid ?? index}
                    sx={{
                        width: "calc(50% - 1rem)",
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        mb: 4,
                        '&:nth-of-type(2n)': {
                            pr: 0
                        }
                    }}
                >
                    <Box
                        sx={{
                            borderRadius: "8px",
                            backgroundColor: (theme) => theme.palette.primary.main,
                            padding: 4,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            flexGrow: 1,
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: "space-between", alignItems: "center" }}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <img src="/logo512teste.png" alt="Logo" style={{ width: 44, height: 32, objectFit: 'cover' }} />
                                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'semibold' }}>
                                    {plano.estrategia}
                                </Typography>
                                <InfoOutlined sx={{ color: "white", alignSelf: "center" }} fontSize="small" />
                            </Box>
                            <BasicMenu
                                onDelete={() => plano.guid && onArquivar(plano.guid)}
                                onEdit={() => plano.guid && onEditar(plano.guid)}
                                isWhite isArchived
                            />
                        </Box>

                        <Box>
                            <Typography sx={{ color: "#607C90", fontSize: "0.75rem" }}>
                                Fator Externo
                            </Typography>
                            <Typography sx={{ color: "white", fontSize: "0.95rem" }}>
                                {plano.fatorExternoDescricao ?? '-'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ color: "#607C90", fontSize: "0.75rem" }}>
                                Fator Interno
                            </Typography>
                            <Typography sx={{ color: "white", fontSize: "0.95rem" }}>
                                {plano.fatorInternoDescricao ?? '-'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ color: "#607C90", fontSize: "0.75rem" }}>
                                Meta Global
                            </Typography>
                            <Typography sx={{ color: "white", fontSize: "0.95rem" }}>
                                {plano.area && plano.metaGlobalDescricao ? `${plano.area}: ${plano.metaGlobalDescricao}` : plano.metaGlobalDescricao ?? '-'}
                            </Typography>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography sx={{ color: "#607C90", fontSize: "0.75rem" }}>
                                Ação
                            </Typography>
                            <Typography sx={{ color: "white", fontSize: "0.95rem" }}>
                                {plano.acao}
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Box>
                                <Typography sx={{ color: "#607C90", fontSize: "0.75rem" }}>
                                    Responsável
                                </Typography>
                                <Typography sx={{ color: "white", fontSize: "0.95rem" }}>
                                    {plano.responsavelNome ?? plano.responsavelGuid}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ color: "#607C90", fontSize: "0.75rem" }}>
                                    Prazo
                                </Typography>
                                <Typography sx={{ color: "white", fontSize: "0.95rem" }}>
                                    {prazoFormatado}
                                </Typography>
                            </Box>
                        </Box>
                        <Box>
                            <Typography sx={{ color: "#607C90", fontSize: "0.75rem" }}>
                                Status
                            </Typography>
                            <Typography sx={{ color: "white", fontSize: "0.95rem" }}>
                                {plano.status ?? '-'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )})}
        </Box>
    );
}
